import apiClient from './apiClient';

export interface ClientResponse {
    id: string;
    fullName: string;
    phone: string;
    email?: string;
    lastVisit?: string;
    notes?: string;
    documentType?: string;
    documentNumber?: string;
    address?: string;
    city?: string;
    cityCode?: string;
    createdAt: string;
    updatedAt: string;
}

export interface PageResponse<T> {
    content: T[];
    totalPages: number;
    totalElements: number;
    size: number;
    number: number;
    first: boolean;
    last: boolean;
    empty: boolean;
}

export interface ClientSummary {
    id: string;
    fullName: string;
    phone: string;
}

export interface ClientCreateRequest {
    salonId: string;
    fullName: string;
    phone: string;
    email?: string;
    notes?: string;
    documentType?: string;
    documentNumber?: string;
    address?: string;
    city?: string;
    cityCode?: string;
}

export interface ClientBulkImportRequest {
    salonId: string;
    clients: {
        fullName: string;
        phone: string;
        email?: string;
        notes?: string;
    }[];
}

export interface BulkImportResponse {
    successCount: number;
    failedCount: number;
    errors: string[];
}

interface ClientCacheData {
    clients: ClientSummary[];
    timestamp: number;
}

const CACHE_KEY = 'cresta_client_cache';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Client API with intelligent caching strategy.
 * Fetches all clients once and caches in localStorage for 1 hour.
 * Local filtering provides 0ms search latency after initial load.
 */
export const clientApi = {
    /**
     * Get all clients for a salon with caching.
     * Checks localStorage first - if data is less than 1 hour old, returns cached version.
     * Otherwise, fetches from API and updates cache.
     *
     * @param salonId salon ID
     * @param forceRefresh if true, bypasses cache and fetches fresh data
     * @returns list of client summaries
     */
    getSalonClients: async (salonId: string, forceRefresh: boolean = false): Promise<ClientSummary[]> => {
        // Check cache first
        if (!forceRefresh) {
            const cached = clientApi.getCachedClients();
            if (cached) {
                console.log(`[ClientCache] Using cached data (${cached.length} clients)`);
                return cached;
            }
        }

        // Cache miss or force refresh - fetch from API
        console.log('[ClientCache] Fetching fresh data from API');
        const response = await apiClient.get<ClientSummary[]>(`/clients/salon/${salonId}/summary`);
        const clients = response.data;

        // Update cache
        clientApi.setCachedClients(clients);

        return clients;
    },

    /**
     * Get ALL clients with FULL details.
     * No caching for now to ensure fresh data for the main list.
     */
    getAllSalonClients: async (salonId: string): Promise<ClientResponse[]> => {
        const response = await apiClient.get<ClientResponse[]>(`/clients/salon/${salonId}`);
        return response.data;
    },

    /**
     * Get clients with pagination.
     */
    getSalonClientsPaged: async (salonId: string, page: number, size: number): Promise<PageResponse<ClientResponse>> => {
        const response = await apiClient.get<PageResponse<ClientResponse>>(`/clients/salon/${salonId}/paged`, {
            params: { page, size }
        });
        return response.data;
    },

    /**
     * Search clients locally (no API call).
     * Filters cached client list by name or phone.
     * Provides instant search results with 0ms latency.
     *
     * @param query search query
     * @returns matching clients
     */
    searchClientsLocally: (query: string): ClientSummary[] => {
        const cached = clientApi.getCachedClients();
        if (!cached || !query || query.trim().length < 2) {
            return [];
        }

        const searchTerm = query.trim().toLowerCase();
        return cached.filter(client =>
            client.fullName.toLowerCase().includes(searchTerm) ||
            client.phone.includes(searchTerm)
        );
    },

    /**
     * Get cached clients from localStorage.
     * Returns null if cache is expired or doesn't exist.
     */
    getCachedClients: (): ClientSummary[] | null => {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (!cached) return null;

            const data: ClientCacheData = JSON.parse(cached);
            const age = Date.now() - data.timestamp;

            if (age > CACHE_TTL_MS) {
                console.log('[ClientCache] Cache expired, clearing');
                localStorage.removeItem(CACHE_KEY);
                return null;
            }

            return data.clients;
        } catch (error) {
            console.error('[ClientCache] Error reading cache:', error);
            return null;
        }
    },

    /**
     * Save clients to localStorage cache.
     */
    setCachedClients: (clients: ClientSummary[]): void => {
        try {
            const data: ClientCacheData = {
                clients,
                timestamp: Date.now()
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(data));
            console.log(`[ClientCache] Cached ${clients.length} clients`);
        } catch (error) {
            console.error('[ClientCache] Error writing cache:', error);
        }
    },

    /**
     * Clear the client cache.
     * Useful after creating a new client.
     */
    clearCache: (): void => {
        localStorage.removeItem(CACHE_KEY);
        console.log('[ClientCache] Cache cleared');
    },

    /**
     * Create a new client and update cache.
     */
    createClient: async (data: ClientCreateRequest): Promise<ClientResponse> => {
        const response = await apiClient.post<ClientResponse>('/clients', data);

        // Clear cache to force refresh on next load
        clientApi.clearCache();

        return response.data;
    },

    /**
     * Bulk import clients.
     */
    bulkImport: async (data: ClientBulkImportRequest): Promise<BulkImportResponse> => {
        const response = await apiClient.post<BulkImportResponse>('/clients/bulk', data);
        clientApi.clearCache();
        return response.data;
    },

    /**
     * Get a client by ID (full details).
     */
    getClientById: async (id: string): Promise<ClientResponse> => {
        const response = await apiClient.get<ClientResponse>(`/clients/${id}`);
        return response.data;
    },
};
